import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography
} from '@mui/material';
import type { ICPAccount } from '../../api/onboarding';
import { useSnackbar } from '../../context/SnackbarContext';
import { OnboardingIcpValidator } from './OnboardingIcpValidator';
import { OnboardingSignalsValidator } from './OnboardingSignalsValidator';
import { OnboardingPrioritizationValidator } from './OnboardingPrioritizationValidator';

export interface IdealCustomerStepProps {
  /** Called after ICP accounts are generated; typically persists onboarding and navigates. */
  onComplete: (result: {
    accounts: ICPAccount[];
    signalInstructions: string[];
    prioritizationValidatedMessage: string;
  }) => void | Promise<void>;
  /** When true, the form is disabled (e.g. while saving the campaign). */
  isSubmitting?: boolean;
}

const STEP_COUNT = 3;
type WizardStep = 0 | 1 | 2;

const IdealCustomerStep: React.FC<IdealCustomerStepProps> = ({
  onComplete,
  isSubmitting = false
}) => {
  const { showSnackbar } = useSnackbar();
  const [step, setStep] = useState<WizardStep>(0);
  const [idealCustomer, setIdealCustomer] = useState('');
  const [signalsText, setSignalsText] = useState('');
  const [prioritizationText, setPrioritizationText] = useState('');
  const [icpSubmitting, setIcpSubmitting] = useState(false);
  const [icpValidated, setIcpValidated] = useState(false);
  const [validatedAccounts, setValidatedAccounts] = useState<ICPAccount[]>([]);
  const [validatedIcpTrim, setValidatedIcpTrim] = useState('');
  const [signalsValidated, setSignalsValidated] = useState(false);
  const [validatedInstructions, setValidatedInstructions] = useState<string[]>(
    []
  );
  const [validatedSignalsTrim, setValidatedSignalsTrim] = useState('');
  const [prioritizationValidated, setPrioritizationValidated] = useState(false);
  const [validatedPrioritizationMessage, setValidatedPrioritizationMessage] =
    useState('');
  const [validatedPrioritizationTrim, setValidatedPrioritizationTrim] =
    useState('');

  const busy = icpSubmitting || isSubmitting;

  const handleIcpValidityChange = useCallback((valid: boolean) => {
    setIcpValidated(valid);
    if (!valid) {
      setValidatedAccounts([]);
      setValidatedIcpTrim('');
    }
  }, []);

  const handleValidatedAccounts = useCallback(
    (accounts: ICPAccount[], icpTrimmed: string) => {
      setValidatedAccounts(accounts);
      setValidatedIcpTrim(icpTrimmed);
    },
    []
  );

  const handleSignalsValidityChange = useCallback((valid: boolean) => {
    setSignalsValidated(valid);
    if (!valid) {
      setValidatedInstructions([]);
      setValidatedSignalsTrim('');
    }
  }, []);

  const handleValidatedInstructions = useCallback(
    (instructions: string[], signalsTrimmed: string) => {
      setValidatedInstructions(instructions);
      setValidatedSignalsTrim(signalsTrimmed);
    },
    []
  );

  const handlePrioritizationValidityChange = useCallback((valid: boolean) => {
    setPrioritizationValidated(valid);
    if (!valid) {
      setValidatedPrioritizationMessage('');
      setValidatedPrioritizationTrim('');
    }
  }, []);

  const handleValidatedPrioritizationMessage = useCallback(
    (message: string, prioritizationTrimmed: string) => {
      setValidatedPrioritizationMessage(message);
      setValidatedPrioritizationTrim(prioritizationTrimmed);
    },
    []
  );

  const handleFinalSubmit = async () => {
    setIcpSubmitting(true);
    try {
      await onComplete({
        accounts: validatedAccounts,
        signalInstructions: validatedInstructions,
        prioritizationValidatedMessage: validatedPrioritizationMessage
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Something went wrong. Try again.';
      showSnackbar(message, 'error');
    } finally {
      setIcpSubmitting(false);
    }
  };

  const canLeaveIcpStep =
    Boolean(idealCustomer.trim()) &&
    icpValidated &&
    validatedAccounts.length > 0 &&
    !busy;

  const canLeaveSignalsStep =
    Boolean(signalsText.trim()) &&
    signalsValidated &&
    validatedInstructions.length > 0 &&
    signalsText.trim() === validatedSignalsTrim &&
    !busy;

  const canFinishStep =
    Boolean(prioritizationText.trim()) &&
    prioritizationValidated &&
    Boolean(validatedPrioritizationMessage) &&
    prioritizationText.trim() === validatedPrioritizationTrim &&
    !busy;

  const handleContinue = () => {
    if (step === 0) {
      if (!canLeaveIcpStep) return;
      setStep(1);
      return;
    }
    if (step === 1) {
      if (!canLeaveSignalsStep) return;
      setStep(2);
      return;
    }
    void handleFinalSubmit();
  };

  const handleBack = () => {
    if (step === 0) return;
    setStep((s) => (s === 2 ? 1 : 0));
  };

  const primaryDisabled =
    step === 0
      ? !canLeaveIcpStep
      : step === 1
        ? !canLeaveSignalsStep
        : !canFinishStep || busy;

  const stepLabel = `Step ${step + 1} of ${STEP_COUNT}`;

  return (
    <>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: 'block', mb: 1 }}
      >
        {stepLabel}
      </Typography>

      {step === 0 && (
        <>
          <Typography
            variant="h5"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            Describe your ideal accounts
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {
              'e.g. "Fintech companies with 200–1000 employees in North America"'
            }
            <br />
            Include industry + company size at minimum for best results.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={5}
            label="Your ideal accounts"
            value={idealCustomer}
            onChange={(e) => setIdealCustomer(e.target.value)}
            disabled={busy}
            sx={{ mb: 0.5 }}
          />
          <OnboardingIcpValidator
            icpText={idealCustomer}
            disabled={busy}
            onValidityChange={handleIcpValidityChange}
            onValidatedAccounts={handleValidatedAccounts}
          />
        </>
      )}

      {step === 1 && (
        <>
          <Typography
            variant="h5"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            Choose signals
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Describe what signals to search for. Strong setups often combine
            department-based signals such as new leadership in a team, hiring in
            a department (for example engineering), or headcount growth in a
            department. More strong signals can include company events like M&A
            or fundraising.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={6}
            label="Signals"
            placeholder='e.g. "Hiring in engineering; new leadership in sales; headcount growth in product; M&A and fundraising"'
            value={signalsText}
            onChange={(e) => setSignalsText(e.target.value)}
            disabled={busy}
            sx={{ mb: 0.5 }}
          />
          <OnboardingSignalsValidator
            signalsText={signalsText}
            disabled={busy}
            onValidityChange={handleSignalsValidityChange}
            onValidatedInstructions={handleValidatedInstructions}
          />
        </>
      )}

      {step === 2 && (
        <>
          <Typography
            variant="h5"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            Prioritization criteria
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            How should we qualify accounts? Specify what makes a company
            prospect qualified or unqualified. Strong prioritization criteria
            include company size, industry, and location. Prioritization can
            also include signals from step 2 in its criteria. Qualifying
            criteria are required, but unqualifying criteria are optional.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={6}
            label="Prioritization criteria"
            placeholder="How should we rank or prioritize accounts?"
            value={prioritizationText}
            onChange={(e) => setPrioritizationText(e.target.value)}
            disabled={busy}
            sx={{ mb: 0.5 }}
          />
          <OnboardingPrioritizationValidator
            prioritizationText={prioritizationText}
            disabled={busy}
            onValidityChange={handlePrioritizationValidityChange}
            onValidatedMessage={handleValidatedPrioritizationMessage}
          />
        </>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: step === 0 ? 'flex-end' : 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          mt: 3
        }}
      >
        {step > 0 && (
          <Button
            type="button"
            variant="outlined"
            onClick={handleBack}
            disabled={busy}
            sx={(theme) => ({
              borderRadius: '50px',
              textTransform: 'capitalize',
              borderColor: theme.palette.divider,
              color: 'text.primary',
              '&:hover': {
                borderColor: theme.palette.text.secondary,
                backgroundColor: 'action.hover'
              }
            })}
          >
            Back
          </Button>
        )}
        <Button
          type="button"
          variant="contained"
          onClick={handleContinue}
          disabled={primaryDisabled}
          sx={(theme) => ({
            border: `1px solid ${theme.customColors?.themePrimary || 'primary.main'}`,
            backgroundColor: theme.customColors?.themePrimary || 'primary.main',
            color: theme.customColors?.themeSecondary || 'white',
            borderRadius: '50px',
            textTransform: 'capitalize',
            '&:disabled': {
              borderColor: 'inherit',
              opacity: 0.6
            }
          })}
        >
          {busy && step === 2 ? (
            <>
              <CircularProgress size={22} color="inherit" sx={{ mr: 1 }} />
              {isSubmitting ? 'Creating campaign…' : 'Working…'}
            </>
          ) : step === 2 ? (
            'Finish'
          ) : (
            'Continue'
          )}
        </Button>
      </Box>
    </>
  );
};

export default IdealCustomerStep;
